import * as React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  Building2,
  ClipboardList,
  Copy,
  MoreHorizontal,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from "@eduviaos/ui-kit";
import type {
  AssessmentTemplateStatus,
  SuperAdminAssessmentTemplateListFilters,
  SuperAdminAssessmentTemplateRow,
} from "@eduviaos/shared";
import { api } from "@/lib/api";
import { mapTemplateStatusToPill } from "@/lib/assessments/statusPill";
import { useSaAssessmentTemplatesCrossTenant } from "@/lib/saAssessmentTemplates/api";
import { CloneTemplateDialog } from "@/components/templates/CloneTemplateDialog";

/**
 * Phase H5c — SA cross-tenant assessment template library.
 *
 * Lists every template across every tenant, with filters for tenant,
 * status, language, and archived visibility. Each row exposes a
 * `Clone to tenant…` action that copies the template into a target
 * tenant as a draft via the SA `POST /super-admin/assessment-templates/
 * {id}/clone` endpoint (Phase H5b BE).
 *
 * This page closes the two H5b FE follow-ups:
 *   1. Cross-tenant matrix UI (this page)
 *   2. Clone modal (the `<CloneTemplateDialog>` child)
 *
 * Iron Rule #1 — backed by H5b BE that is integration-tested.
 * Iron Rule #16 — uses `<DataTable>` + `<StatusPill>` primitives, sticky
 * head, action column last + ⋯ dropdown, skeleton (not spinner) loading,
 * dedicated empty-state component.
 */

const ANY = "__any__";
const ALL_TENANTS = "__all__";

type StatusFilterValue = AssessmentTemplateStatus | typeof ANY;
type LanguageFilterValue = "en" | "hi" | typeof ANY;

interface TenantRow {
  slug: string;
  name: string;
  type: "school" | "coaching_institute" | "study_centre";
  status: "active" | "suspended" | "archived";
  created_at: string;
}

interface TenantListResponse {
  items: TenantRow[];
  total: number;
}

function useTenants() {
  return useQuery({
    queryKey: ["super-admin", "tenants", "library"],
    queryFn: () => api.get<TenantListResponse>("/super-admin/tenants"),
    staleTime: 60_000,
  });
}

export function AssessmentTemplatesLibraryPage() {
  const [search, setSearch] = React.useState("");
  const [tenantFilter, setTenantFilter] = React.useState<string>(ALL_TENANTS);
  const [statusFilter, setStatusFilter] = React.useState<StatusFilterValue>(ANY);
  const [languageFilter, setLanguageFilter] = React.useState<LanguageFilterValue>(ANY);
  const [includeArchived, setIncludeArchived] = React.useState(false);
  const [cloneSource, setCloneSource] = React.useState<SuperAdminAssessmentTemplateRow | null>(
    null,
  );
  const [flashMessage, setFlashMessage] = React.useState<string | null>(null);

  const tenantsQuery = useTenants();
  const tenants = React.useMemo(() => tenantsQuery.data?.items ?? [], [tenantsQuery.data?.items]);

  const filters = React.useMemo<SuperAdminAssessmentTemplateListFilters>(() => {
    const f: SuperAdminAssessmentTemplateListFilters = { limit: 200 };
    if (tenantFilter !== ALL_TENANTS) f.tenant_id = tenantFilter;
    if (statusFilter !== ANY) f.status = statusFilter;
    if (languageFilter !== ANY) f.language = languageFilter;
    // The BE hides `archived` by default; opt in explicitly so the SA
    // can audit retired templates from one place.
    if (includeArchived || statusFilter === "archived") f.include_archived = true;
    const term = search.trim();
    if (term) f.search = term;
    return f;
  }, [tenantFilter, statusFilter, languageFilter, includeArchived, search]);

  const templatesQuery = useSaAssessmentTemplatesCrossTenant(filters);

  const items = React.useMemo(
    () => sortLibraryRows(templatesQuery.data?.items ?? []),
    [templatesQuery.data?.items],
  );
  const total = templatesQuery.data?.total ?? 0;

  const filtersActive =
    search.trim() !== "" ||
    tenantFilter !== ALL_TENANTS ||
    statusFilter !== ANY ||
    languageFilter !== ANY;

  React.useEffect(() => {
    if (!flashMessage) return;
    const timer = window.setTimeout(() => setFlashMessage(null), 5000);
    return () => window.clearTimeout(timer);
  }, [flashMessage]);

  const clearFilters = () => {
    setSearch("");
    setTenantFilter(ALL_TENANTS);
    setStatusFilter(ANY);
    setLanguageFilter(ANY);
  };

  return (
    <div className="mx-auto max-w-7xl py-8" data-testid="sa-templates-library">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Assessment templates</PageHeaderHeading>
            <PageHeaderDescription>
              Cross-tenant library of every assessment template on the platform. Clone any template
              into another tenant to seed it as a draft — the source stays untouched.
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void templatesQuery.refetch()}
              disabled={templatesQuery.isFetching}
              aria-label="Refresh templates"
            >
              <RefreshCw
                aria-hidden="true"
                className={cn("size-4", templatesQuery.isFetching && "animate-spin")}
              />
              Refresh
            </Button>
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      {flashMessage ? (
        <div
          role="status"
          className="mt-4 flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300"
        >
          <span className="flex-1">{flashMessage}</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-0.5 text-xs"
            onClick={() => setFlashMessage(null)}
          >
            Dismiss
          </Button>
        </div>
      ) : null}

      {templatesQuery.isError ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">
            Couldn't load the template library
            {templatesQuery.error?.message ? `: ${templatesQuery.error.message}` : "."}
          </span>
        </div>
      ) : null}

      <DataTable className="mt-6">
        <DataTableToolbar>
          <div className="relative w-full sm:w-64">
            <Search
              aria-hidden="true"
              className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2"
            />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by name…"
              className="pl-8"
              aria-label="Filter templates by name"
            />
          </div>
          <Select value={tenantFilter} onValueChange={setTenantFilter}>
            <SelectTrigger className="h-9 w-44" aria-label="Tenant filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_TENANTS}>All tenants</SelectItem>
              {tenants.map((t) => (
                <SelectItem key={t.slug} value={t.slug}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as StatusFilterValue)}
          >
            <SelectTrigger className="h-9 w-32" aria-label="Status filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={languageFilter}
            onValueChange={(v) => setLanguageFilter(v as LanguageFilterValue)}
          >
            <SelectTrigger className="h-9 w-28" aria-label="Language filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any lang</SelectItem>
              <SelectItem value="en">EN</SelectItem>
              <SelectItem value="hi">HI</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Checkbox
              id="library-include-archived"
              checked={includeArchived}
              onCheckedChange={(v) => setIncludeArchived(v === true)}
            />
            <Label
              htmlFor="library-include-archived"
              className="text-muted-foreground text-xs font-normal"
            >
              Show archived
            </Label>
          </div>
          {filtersActive ? (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X aria-hidden="true" className="size-3.5" />
              Clear
            </Button>
          ) : null}
          <DataTableToolbarSpacer />
          <span className="text-muted-foreground text-xs tabular-nums">
            {total.toLocaleString()} total
          </span>
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[18%]">Tenant</TableHead>
                <TableHead className="w-[26%]">Template</TableHead>
                <TableHead className="w-[8%]">Grades</TableHead>
                <TableHead className="w-[6%]">Lang</TableHead>
                <TableHead className="w-[8%] text-right">Items</TableHead>
                <TableHead className="w-[10%]">Status</TableHead>
                <TableHead className="w-[14%]">Updated</TableHead>
                <TableHead className="w-[10%] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            {templatesQuery.isLoading ? (
              <DataTableSkeleton rows={6} columns={8} />
            ) : (
              <TableBody>
                {items.map((row) => (
                  <LibraryRow key={row.id} row={row} onClone={(target) => setCloneSource(target)} />
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!templatesQuery.isLoading && items.length === 0 ? (
          <DataTableEmpty
            icon={<ClipboardList aria-hidden="true" />}
            title={filtersActive ? "No matches" : "No templates yet"}
            description={
              filtersActive
                ? "Adjust filters or clear them to see every template across the platform."
                : "Templates appear here as soon as any tenant authors one. Use Clone to seed a tenant from another's blueprint."
            }
            action={
              filtersActive ? (
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  <X aria-hidden="true" className="size-3.5" />
                  Clear filters
                </Button>
              ) : null
            }
          />
        ) : null}

        {!templatesQuery.isLoading && items.length > 0 ? (
          <DataTableFooter>
            <span className="text-muted-foreground text-sm tabular-nums">
              {items.length.toLocaleString()} shown
            </span>
          </DataTableFooter>
        ) : null}
      </DataTable>

      <Card className="bg-muted/40 mt-4">
        <CardContent className="text-muted-foreground py-3 text-xs">
          <strong className="text-foreground">Clone semantics:</strong> a clone always lands as a{" "}
          <code className="text-foreground">draft</code> in the target tenant. The source's status,
          blueprint, and history stay untouched. Cloning into the same tenant requires a distinct
          slug, which is useful for "fork-as-starter" workflows.
        </CardContent>
      </Card>

      <CloneTemplateDialog
        source={cloneSource}
        tenants={tenants}
        onOpenChange={(open) => {
          if (!open) setCloneSource(null);
        }}
        onCloned={(targetTenantName, clonedName) => {
          setFlashMessage(
            `Cloned "${clonedName}" into ${targetTenantName}. The new draft is live in the target tenant.`,
          );
          setCloneSource(null);
        }}
      />
    </div>
  );
}

function LibraryRow({
  row,
  onClone,
}: {
  row: SuperAdminAssessmentTemplateRow;
  onClone: (source: SuperAdminAssessmentTemplateRow) => void;
}) {
  const tenantLabel = row.tenant_name ?? `(orphaned: ${row.tenant_id})`;
  const updatedAt = formatDate(row.updated_at);
  const updatedAtIso = row.updated_at;

  return (
    <TableRow>
      <TableCell>
        <Link
          to={`/tenants/${encodeURIComponent(row.tenant_id)}`}
          className="group inline-flex items-start gap-2 text-left"
        >
          <Building2
            aria-hidden="true"
            className="text-muted-foreground mt-0.5 size-3.5 shrink-0"
          />
          <span className="min-w-0">
            <span className="text-foreground block truncate text-sm font-medium group-hover:underline group-focus-visible:underline">
              {tenantLabel}
            </span>
            <span className="text-muted-foreground block truncate font-mono text-[10px]">
              {row.tenant_id}
            </span>
          </span>
        </Link>
      </TableCell>
      <TableCell>
        <div className="min-w-0">
          <span className="text-foreground block truncate text-sm font-medium">{row.name}</span>
          <span className="text-muted-foreground block truncate font-mono text-[11px]">
            {row.slug}
          </span>
          {row.description ? (
            <span className="text-muted-foreground line-clamp-1 text-[11px] italic">
              {row.description}
            </span>
          ) : null}
        </div>
      </TableCell>
      <TableCell className="text-xs tabular-nums">
        {row.grade_band.min}–{row.grade_band.max}
      </TableCell>
      <TableCell className="text-xs uppercase">{row.language}</TableCell>
      <TableCell className="text-right text-xs tabular-nums">{row.item_count}</TableCell>
      <TableCell>
        <StatusPill status={mapTemplateStatusToPill(row.status)} />
      </TableCell>
      <TableCell className="text-muted-foreground text-xs tabular-nums">
        <time dateTime={updatedAtIso} title={updatedAtIso}>
          {updatedAt}
        </time>
      </TableCell>
      <TableCell className="w-[1%] text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label={`Open actions for ${row.name}`}
            >
              <MoreHorizontal aria-hidden="true" className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onClone(row)}>
              <Copy aria-hidden="true" className="size-3.5" />
              Clone to tenant…
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

/**
 * Library sort order is the same as the picker (Phase H5b): active
 * first (those the SA is most likely to clone), then draft, then
 * archived. Within a status group, sort by tenant name (case-insensitive)
 * and then by template name so adjacent rows from the same tenant
 * cluster together.
 */
function sortLibraryRows(
  rows: SuperAdminAssessmentTemplateRow[],
): SuperAdminAssessmentTemplateRow[] {
  const rank: Record<string, number> = { active: 0, draft: 1, archived: 2 };
  return [...rows].sort((a, b) => {
    const ra = rank[a.status] ?? 99;
    const rb = rank[b.status] ?? 99;
    if (ra !== rb) return ra - rb;
    const ta = (a.tenant_name ?? a.tenant_id).toLocaleLowerCase();
    const tb = (b.tenant_name ?? b.tenant_id).toLocaleLowerCase();
    if (ta !== tb) return ta.localeCompare(tb);
    return a.name.localeCompare(b.name);
  });
}

function formatDate(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
